import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Search, Type, FileText, CreditCard, Paperclip, Image as ImageIcon, Code, FunctionSquare, List, Download, Trash2, Bold, Italic, Underline } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const NewDoc = () => {
  const [title, setTitle] = useState('Page Title');
  const [isEditing, setIsEditing] = useState(false);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [activeTool, setActiveTool] = useState(null);
  const [images, setImages] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [showFormatting, setShowFormatting] = useState(false);
  const [codeBlocks, setCodeBlocks] = useState([]);
  const [showTexEditor, setShowTexEditor] = useState(false);
  const [texFormula, setTexFormula] = useState('');
  
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const texEditorRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target)) {
        setContextMenu(null);
      }
      if (texEditorRef.current && !texEditorRef.current.contains(e.target)) {
        closeTexEditor();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleLeftSidebar = () => setLeftSidebarOpen(!leftSidebarOpen);
  const toggleRightSidebar = () => setRightSidebarOpen(!rightSidebarOpen);

  const handleTitleClick = () => setIsEditing(true);
  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleTitleBlur = () => setIsEditing(false);
  const handleToolClick = (tool) => setActiveTool(activeTool === tool ? null : tool);

  const handleEditorChange = () => {
    // We don't need to update any state here, as the contentEditable div manages its own content
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
        
          let newWidth = img.width;
          let newHeight = img.height;
          
          if (newWidth > 300 || newHeight > 300) {
            if (newWidth > newHeight) {
              newHeight = (newHeight / newWidth) * 300;
              newWidth = 300;
            } else {
              newWidth = (newWidth / newHeight) * 300;
              newHeight = 300;
            }
          }
        
          canvas.width = newWidth;
          canvas.height = newHeight;
        
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
        
          const resizedImage = canvas.toDataURL(file.type);
        
          const newImage = {
            id: Date.now(),
            src: resizedImage,
            originalSrc: event.target.result
          };
        
          setImages([...images, newImage]);
          
          // Insert the image at the current cursor position
          const selection = window.getSelection();
          const range = selection.getRangeAt(0);
          const fragment = range.createContextualFragment(`<img src="${resizedImage}" alt="Uploaded image ${newImage.id}" class="max-w-[300px] max-h-[300px]" />`);
          range.deleteContents();
          range.insertNode(fragment);
          range.collapse();
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please select an image file.');
    }
  };

  const triggerImageUpload = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    fileInputRef.current.click();
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    const selectedNode = window.getSelection().anchorNode;
    const clickedElement = selectedNode.nodeType === 1 ? selectedNode : selectedNode.parentElement;
    
    if (clickedElement.tagName === 'IMG') {
      const imageId = clickedElement.alt.match(/\d+/)[0];
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        imageId: imageId
      });
    }
  };

  const downloadOriginalImage = (imageId) => {
    const image = images.find(img => img.id === parseInt(imageId));
    if (image) {
      const link = document.createElement('a');
      link.href = image.originalSrc;
      link.download = `original_image_${imageId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setContextMenu(null);
  };

  const deleteImage = (imageId) => {
    setImages(images.filter(img => img.id !== parseInt(imageId)));
    const imgToDelete = editorRef.current.querySelector(`img[alt="Uploaded image ${imageId}"]`);
    if (imgToDelete) {
      imgToDelete.remove();
    }
    setContextMenu(null);
  };

  const applyTextFormat = (format) => {
    document.execCommand(format, false, null);
  };

  const handleSelectionChange = () => {
    const selection = window.getSelection();
    setShowFormatting(selection.toString().length > 0);
  };

  const addCodeBlock = () => {
    const newCodeBlock = {
      id: Date.now(),
      language: 'javascript',
      code: '// Start coding here'
    };
    setCodeBlocks(prevBlocks => [...prevBlocks, newCodeBlock]);
    const codeBlockPlaceholder = `<div data-code-block-id="${newCodeBlock.id}">[Code Block]</div>`;
    document.execCommand('insertHTML', false, codeBlockPlaceholder);
  };

  const handleLanguageChange = (id, language) => {
    setCodeBlocks(prevBlocks => 
      prevBlocks.map(block => 
        block.id === id ? { ...block, language } : block
      )
    );
  };

  const handleCodeChange = (id, newCode) => {
    setCodeBlocks(prevBlocks => 
      prevBlocks.map(block => 
        block.id === id ? { ...block, code: newCode } : block
      )
    );
  };

  const openTexEditor = () => {
    setShowTexEditor(true);
  };

  const closeTexEditor = () => {
    setShowTexEditor(false);
    setTexFormula('');
  };

  const handleTexFormulaChange = (e) => {
    setTexFormula(e.target.value);
  };

  const insertTexFormula = () => {
    try {
      const renderedFormula = katex.renderToString(texFormula, {
        throwOnError: false,
        displayMode: true,
      });
      const formulaId = Date.now();
      const formulaHTML = `<span class="tex-formula" data-formula-id="${formulaId}">${renderedFormula}</span>`;
    
      // Insert the formula at the current cursor position
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const fragment = range.createContextualFragment(formulaHTML);
      range.deleteContents();
      range.insertNode(fragment);
      range.collapse();
    
      closeTexEditor();
    } catch (error) {
      console.error('Error rendering TeX formula:', error);
      alert('Error rendering TeX formula. Please check your input.');
    }
  };

  const focusTextArea = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const CodeBlock = React.memo(({ block, onLanguageChange, onCodeChange }) => {
    const [localCode, setLocalCode] = useState(block.code);

    useEffect(() => {
      setLocalCode(block.code);
    }, [block.code]);

    const handleLocalCodeChange = (e) => {
      const newCode = e.target.value;
      setLocalCode(newCode);
    };

    const handleBlur = () => {
      onCodeChange(block.id, localCode);
    };

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden my-4">
        <div className="bg-gray-100 px-4 py-2 flex justify-between items-center">
          <select 
            className="bg-transparent text-sm text-gray-700"
            value={block.language}
            onChange={(e) => onLanguageChange(block.id, e.target.value)}
          >
            <option value="javascript">JavaScript</option>
            <option value="python">Python</option>
            <option value="java">Java</option>
          </select>
        </div>
        <div className="relative">
          <div className="absolute top-0 left-0 px-4 py-2 text-sm font-semibold text-gray-500">
            {block.language.charAt(0).toUpperCase() + block.language.slice(1)}
          </div>
          <textarea
            className="w-full min-h-[100px] p-4 pt-10 font-mono text-sm resize-y focus:outline-none"
            value={localCode}
            onChange={handleLocalCodeChange}
            onBlur={handleBlur}
          />
        </div>
      </div>
    );
  });

  const insertOptions = [
    { icon: Type, label: 'Text', action: focusTextArea },
    { icon: FileText, label: 'Page', action: () => {} },
    { icon: CreditCard, label: 'Card', action: () => {} },
    { icon: Paperclip, label: 'File Attachment', action: () => {} },
    { icon: ImageIcon, label: 'Image', action: triggerImageUpload },
    { icon: Code, label: 'Code Block', action: addCodeBlock },
    { icon: FunctionSquare, label: 'TeX Formula', action: openTexEditor },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      <div className="flex flex-1 relative">
        <div className={`w-64 bg-white shadow-md transition-all duration-300 ${leftSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex justify-around p-4 border-b border-gray-200">
            <button className={`p-2 rounded-md ${activeTool === 'toc' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => handleToolClick('toc')}>
              <List size={20} />
            </button>
            <button className={`p-2 rounded-md ${activeTool === 'attachments' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => handleToolClick('attachments')}>
              <Paperclip size={20} />
            </button>
            <button className={`p-2 rounded-md ${activeTool === 'search' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`} onClick={() => handleToolClick('search')}>
              <Search size={20} />
            </button>
          </div>
          {activeTool && (
            <div className="p-4">
              <h3 className="text-lg font-semibold mb-2">{activeTool === 'toc' ? 'Table of Contents' : activeTool === 'attachments' ? 'Attachments' : 'Search'}</h3>
              <p className="text-sm text-gray-600">{activeTool === 'toc' ? 'Use titles, pages or cards to create a table of contents.' : activeTool === 'attachments' ? 'No attachments yet.' : 'Search your document...'}</p>
            </div>
          )}
        </div>
        <button 
          className="absolute top-1/2 left-0 transform -translate-y-1/2 bg-white rounded-r-full p-2 shadow-md z-10"
          onClick={toggleLeftSidebar}
        >
          {leftSidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </button>
        <div className="flex-1 p-8 overflow-y-auto">
          {isEditing ? (
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              autoFocus
              className="text-3xl font-bold mb-6 w-full bg-transparent border-none focus:outline-none"
            />
          ) : (
            <h1 onClick={handleTitleClick} className="text-3xl font-bold mb-6 cursor-text">{title}</h1>
          )}
          <div className={`fixed z-10 bg-white rounded-md shadow-md p-1 ${showFormatting ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-200`}>
            <button onClick={() => applyTextFormat('bold')} className="p-1 hover:bg-gray-100 rounded"><Bold size={16} /></button>
            <button onClick={() => applyTextFormat('italic')} className="p-1 hover:bg-gray-100 rounded"><Italic size={16} /></button>
            <button onClick={() => applyTextFormat('underline')} className="p-1 hover:bg-gray-100 rounded"><Underline size={16} /></button>
          </div>
          <div 
            ref={editorRef}
            className="min-h-[500px] p-4 bg-white rounded-lg shadow-sm focus:outline-none"
            contentEditable={true}
            onInput={handleEditorChange}
            onKeyDown={handleKeyDown}
            onContextMenu={handleContextMenu}
            onMouseUp={handleSelectionChange}
            onKeyUp={handleSelectionChange}
          />
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleImageUpload}
            accept="image/*"
          />
          {codeBlocks.map(block => (
            <CodeBlock
              key={block.id}
              block={block}
              onLanguageChange={handleLanguageChange}
              onCodeChange={handleCodeChange}
            />
          ))}
        </div>
        <div className={`w-64 bg-white shadow-md transition-all duration-300 ${rightSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-2">Insert</h3>
            <p className="text-sm text-gray-600 mb-4">Click any item to insert into the document</p>
            <ul className="space-y-2">
              {insertOptions.map((item, index) => (
                <li key={index} onClick={item.action} className="flex items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                  <item.icon size={20} className="mr-3 text-gray-600" />
                  <span className="text-sm font-medium">{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <button 
          className="absolute top-1/2 right-0 transform -translate-y-1/2 bg-white rounded-l-full p-2 shadow-md z-10"
          onClick={toggleRightSidebar}
        >
          {rightSidebarOpen ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>
      {contextMenu && (
        <div className="fixed bg-white rounded-md shadow-lg z-20" style={{ top: contextMenu.y, left: contextMenu.x }}>
          <button onClick={() => downloadOriginalImage(contextMenu.imageId)} className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100">
            <Download size={16} className="mr-2" /> Download Original
          </button>
          <button onClick={() => deleteImage(contextMenu.imageId)} className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100">
            <Trash2 size={16} className="mr-2" /> Delete Image
          </button>
        </div>
      )}
      {showTexEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
          <div className="bg-white p-6 rounded-lg w-96" ref={texEditorRef}>
            <h3 className="text-lg font-semibold mb-4">Insert TeX Formula</h3>
            <textarea
              value={texFormula}
              onChange={handleTexFormulaChange}
              placeholder="Enter your TeX formula here..."
              className="w-full h-32 p-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end space-x-2">
              <button onClick={insertTexFormula} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Insert</button>
              <button onClick={closeTexEditor} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDoc;